import {
    checkAndSetupIframeIntegration,
    COMMON_QUERY_NAMES,
    CommonLocalisationArgument,
    DEFAULT_LANGUAGE_CODE,
    getQueryParamDictionary,
    IframeIntegrationOptions,
    IframeIntegrationState,
    MODES,
    useTranslationStore
} from "@swisstiming/webtec-kit";
import { merge } from "lodash-es";
import { defineStore } from "pinia";
import { computed, reactive } from "vue";

import OwlStrings from "../localisation/en.json";

export interface IOwlStoreOptions {
    iframeIntegration: IframeIntegrationOptions;
}

interface IOwlConfig {
    mode?: MODES;
    lng?: CommonLocalisationArgument;
    profile?: string;
    iframeIntegration: IframeIntegrationState;
}

export const useOwlStore = defineStore("OwlStore", () => {
    // https://github.com/vuejs/pinia/issues/978 StoreSetup
    // https://pinia.vuejs.org/introduction.html#basic-example

    const { setStrings } = useTranslationStore();

    // <editor-fold desc="State">

    const config: IOwlConfig = reactive({
        mode: computed(() => (qParams.value.mode ?? import.meta.env.MODE) as MODES),
        lng: computed(() => (qParams.value.lng ?? DEFAULT_LANGUAGE_CODE) as CommonLocalisationArgument),
        profile: computed(() => qParams.value.profile?.toLowerCase()),
        iframeIntegration: { required: false, domains: [], authorized: false }
    });

    // </editor-fold>

    // <editor-fold desc="Getters">
    const qParams = computed(() => getQueryParamDictionary<COMMON_QUERY_NAMES>());
    // </editor-fold>

    // <editor-fold desc="Actions">

    const requestTranslation = async () => {
        try {
            if (config.lng !== DEFAULT_LANGUAGE_CODE) {
                // get english too to be able to fallback to english values.
                const localeEn = await fetch(`/localisation/${DEFAULT_LANGUAGE_CODE}.json`);
                const localeEnJson = await localeEn.json();

                setStrings(merge(OwlStrings, localeEnJson));
            }

            const locale = await fetch(`/localisation/${config.lng}.json`);
            const localeJson = await locale.json();

            setStrings(merge(OwlStrings, localeJson));
        } catch (err) {
            console.error(`Failed to request locales of given language: ${config.lng}`, err);
        }
    };

    // Request translations
    requestTranslation();

    const initOwlStore = async (options: IOwlStoreOptions) => {
        // set profile data-attribute on html-element, which can be utilized for theming, etc.
        if (qParams.value.profile) {
            document.documentElement.setAttribute("data-profile", qParams.value.profile);
        } else {
            document.documentElement.setAttribute("data-profile", "default");
        }

        config.iframeIntegration = checkAndSetupIframeIntegration(options.iframeIntegration, (options) => {
            const isViteDevEnvironment = import.meta.env.DEV;
            const isModeDev = config.mode === MODES.DEV || config.mode === MODES.DEV_SHORT;
            const isModeUAT = config.mode === MODES.UAT;
            return isViteDevEnvironment || isModeDev || isModeUAT ? false : options.required;
        });
    };

    // </editor-fold>

    return {
        config,
        qParams,
        initOwlStore
    };
});
